import React from 'react';
import { 
  TrendingUp, 
  ExternalLink, 
  Heart, 
  Repeat, 
  Info,
  User,
  CheckCircle2,
  Eye,
  Bookmark,
  Percent,
  MessageSquare,
  Clock,
  ChevronRight
} from 'lucide-react';
import moment from 'moment';
import { cn } from "../../lib/utils";

// shadcn UI
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Separator } from "../ui/separator";
import SimulatedTweet from '../SimulatedTweet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

interface Evidence {
  text: string;
  url?: string;
  date?: string;
  authorName?: string;
  authorHandle?: string;
  authorAvatar?: string;
  mediaUrl?: string;
  likeCount?: number;
  retweetCount?: number;
}

interface Trend {
  hashtag: string;
  name: string;
  description: string;
  template?: string;
  evidences?: Evidence[];
  lastSeen?: string;
  weight?: number;
  id?: string;
  isCompleted?: boolean;
  isGeneric?: boolean;
}

interface TrendsBoxProps {
  trends: Trend[];
  dayPosts?: any[];
  title?: string;
  onTagClick?: (hashtag: string) => void;
  onTrendClick?: (trend: Trend) => void;
  onPostClick?: (postId: string) => void;
}


export default function TrendsBox({ 
  trends, 
  dayPosts = [], 
  title = 'Market Trends', 
  onTagClick, 
  onTrendClick,
  onPostClick
}: TrendsBoxProps): JSX.Element {
  const completedTrends = trends.filter(t => t.isCompleted);
  
  // Categorize posts: those related to trends and others
  const trendPostIds = new Set(completedTrends.map(t => t.id).filter(Boolean));

  return (
    <div className="p-6 flex flex-col space-y-6">
      {/* 1. Daily Tasks & Completion Status */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-primary">
            <TrendingUp className="h-5 w-5" strokeWidth={3} />
            <h4 className="font-black text-sm uppercase tracking-widest text-foreground">{title}</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Progress</span>
            <Badge className={cn(
              "font-black text-[10px]",
              completedTrends.length === trends.length && trends.length > 0 
                ? "bg-green-600 text-white" 
                : "bg-primary/10 text-primary border border-primary/30"
            )}>
              {completedTrends.length}/{trends.length} COMPLETED
            </Badge>
          </div>
        </div>

        {trends.length === 0 ? (
          <div className="bg-background rounded-2xl p-8 border border-border border-dashed text-center">
            <p className="text-muted-foreground text-xs italic">No relevant trends detected for this period.</p>
          </div>
        ) : (
          <div className="space-y-3">
              {trends.map((trend, index) => {
                const trendItem = (
                  <div 
                    className={cn(
                      "group flex items-center justify-between p-3 bg-background rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all duration-300 cursor-pointer",
                      trend.isCompleted && "border-green-500/30 bg-green-500/5"
                    )}
                    onClick={() => onTrendClick?.(trend)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {trend.isCompleted ? (
                        <div className="bg-green-500 rounded-full p-1 shadow-lg shadow-green-500/20">
                          <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
                        </div>
                      ) : trend.isGeneric && (
                        <Badge 
                          className="bg-primary hover:bg-primary/90 text-white font-black text-[9px] h-6 px-2 cursor-pointer shadow-lg shadow-primary/10 transition-transform active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTagClick?.(trend.hashtag);
                          }}
                        >
                          {trend.hashtag}
                        </Badge>
                      )}
                      <span className={cn(
                        "text-xs font-black truncate group-hover:text-foreground transition-colors",
                        trend.isCompleted ? "text-green-400" : "text-foreground"
                      )}>
                        {trend.name}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      {(trend.lastSeen || trend.weight) && (
                        <div className="flex gap-2 text-[10px] font-black uppercase tracking-tighter">
                          {trend.lastSeen && <span className="text-muted-foreground">{trend.lastSeen}</span>}
                          {trend.weight !== undefined && <span className="text-primary">W:{trend.weight}</span>}
                        </div>
                      )}
                      {trend.evidences?.[0]?.url && (
                        <div className="text-muted-foreground group-hover:text-primary transition-colors">
                          <Info className="h-3.5 w-3.5" />
                        </div>
                      )}
                    </div>
                  </div>
                );

                if (trend.evidences && trend.evidences.length > 0) {
                  return (
                    <HoverCard key={index} openDelay={200}>
                      <HoverCardTrigger asChild>
                        <div className="cursor-help">{trendItem}</div>
                      </HoverCardTrigger>
                      <HoverCardContent side="left" align="start" sideOffset={15} className="p-0 bg-black border-border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 w-[320px]">
                        <ScrollArea className="max-h-[500px]">
                          <div className="flex flex-col">
                            {trend.template && (
                              <div className="px-4 py-3 bg-primary/10 border-b border-primary/20">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <MessageSquare className="h-3 w-3 text-primary" />
                                  <span className="text-[9px] font-black uppercase tracking-widest text-primary">Copy this format</span>
                                </div>
                                <p className="text-xs font-mono text-primary/90 leading-relaxed">{trend.template}</p>
                              </div>
                            )}
                            {trend.evidences.map((ev, i) => (
                              <React.Fragment key={i}>
                                <div className="relative border-b border-border/50 last:border-0">
                                  <div className="flex justify-between items-center bg-muted/30 px-4 py-2">
                                    {i === 0 ? (
                                      <div className="flex items-center gap-1.5">
                                        <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                                        <span className="text-[9px] font-black uppercase tracking-widest text-primary">Most Popular Example</span>
                                      </div>
                                    ) : trend.evidences && i === trend.evidences.length - 1 && trend.evidences.length > 1 ? (
                                      <div className="flex items-center gap-1.5">
                                        <div className="h-1.5 w-1.5 rounded-full bg-slate-500" />
                                        <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Most Recent Example</span>
                                      </div>
                                    ) : (
                                      <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Example {i + 1}</span>
                                    )}
                                  </div>
                                  <SimulatedTweet text={ev.text} url={ev.url} date={ev.date} authorName={ev.authorName} authorHandle={ev.authorHandle} authorAvatar={ev.authorAvatar} mediaUrl={ev.mediaUrl} likeCount={ev.likeCount} retweetCount={ev.retweetCount} />
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        </ScrollArea>
                      </HoverCardContent>
                    </HoverCard>
                  );
                }
                return <div key={index}>{trendItem}</div>;
              })}
          </div>
        )}
      </div>
    </div>
  );
}
